import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ModelType } from '../../utils/constants/sharedConstants';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { getSelectedModelType, setModelType } from '../../utils/server/storageUtils';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useAuth } from '../../../context/AuthContext';

export function ModelSelectionButton() {
    const { t, tr } = useAppTranslation();
    const { token } = useAuth();
    const [modelType, setModelTypeState] = useState(ModelType.THINKING ?? 'thinking');
    const [isLoadingModelType, setIsLoadingModelType] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [scopeVersion, setScopeVersion] = useState(0);

    useEffect(() => {
        const handleScopeChange = () => setScopeVersion((current) => current + 1);

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, []);

    useEffect(() => {
        if (!token) return;

        const fetchModelType = async () => {
            setIsLoadingModelType(true);
            try {
                const currentModelType = await getSelectedModelType(token);
                if (currentModelType) {
                    setModelTypeState(currentModelType);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoadingModelType(false);
            }
        };

        fetchModelType();
    }, [scopeVersion, token]);

    const handleModelTypeChange = async (event) => {
        const newModel = event.target.value;

        if (!token) {
            showToastError(t.pleaseLogin);
            return;
        }

        setIsSaving(true);
        try {
            await setModelType(newModel, token);
            setModelTypeState(newModel);
            showToastSuccess(tr('modelUpdatedTo', { model: getTextForModelType(newModel) }));
        } catch (error) {
            console.error(error);
            showToastError(t.errorSaveModel);
        } finally {
            setIsSaving(false);
        }
    };

    function getTextForModelType(type) {
        switch (type) {
            case ModelType.FAST:
                return t.modelFast;
            case ModelType.THINKING:
                return t.modelThinking;
            default:
                return t.modelUnknown;
        }
    }

    return (
        <Box>
            <FormControl
                fullWidth
                sx={{
                    width: { xs: '100%', sm: 260 },
                    minWidth: { xs: '100%', sm: 260 },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        backgroundColor: 'var(--surface-fill-strong)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: 'var(--accent-color)',
                    },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--accent-color) !important',
                    },
                }}
            >
                <InputLabel id='model-type-label'>{t.modelLabel}</InputLabel>
                <Select
                    labelId='model-type-label'
                    value={modelType}
                    label={t.modelLabel}
                    onChange={handleModelTypeChange}
                    disabled={!token || isLoadingModelType || isSaving}
                >
                    {Object.values(ModelType).map((type) => (
                        <MenuItem key={type} value={type}>
                            {getTextForModelType(type)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
}
